import { useState, useEffect, useRef } from 'react';
import { matrixAPI, seriesAPI, divisionsAPI, teamsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Spinner } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

// ─── Restriction colors ───────────────────────────────────────────────────────
const RESTRICTION_COLORS = {
  'August only':   { bg: 'rgba(245,166,35,.18)',  text: 'var(--gold)',   border: 'rgba(245,166,35,.5)' },
  'July only':     { bg: 'rgba(59,130,246,.18)',  text: 'var(--blue)',   border: 'rgba(59,130,246,.5)' },
  'June only':     { bg: 'rgba(168,85,247,.18)',  text: 'var(--purple)', border: 'rgba(168,85,247,.5)' },
  'Must not play': { bg: 'rgba(239,68,68,.2)',    text: 'var(--red)',    border: 'rgba(239,68,68,.5)'  },
  'Home only':     { bg: 'rgba(0,200,150,.18)',   text: 'var(--accent)', border: 'rgba(0,200,150,.5)'  },
  'Away only':     { bg: 'rgba(0,200,150,.09)',   text: 'var(--accent)', border: 'rgba(0,200,150,.3)'  },
  'Custom':        { bg: 'rgba(245,166,35,.09)',  text: 'var(--gold)',   border: 'rgba(245,166,35,.3)' },
};
const RESTRICTION_OPTS = Object.keys(RESTRICTION_COLORS);

// ─── localStorage helpers ─────────────────────────────────────────────────────
// Key: matrixOrder_<divisionId>_<pool|none>  →  JSON array of short_name strings (Excel column order)
const lsGet = (key) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; } };
const lsSet = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

// ─── Excel parser ─────────────────────────────────────────────────────────────
function loadSheetJS() {
  return new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    s.onload = () => resolve(window.XLSX);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function parsePool(ws, titleRow) {
  const ref = ws['!ref'];
  if (!ref) return { teams: [], pairs: [] };
  const range = window.XLSX.utils.decode_range(ref);
  const headerRow = [];
  for (let c = 2; c <= range.e.c; c++) {
    const cell = ws[window.XLSX.utils.encode_cell({ r: titleRow, c })];
    const v = cell ? String(cell.v || '').trim() : '';
    if (v && !v.startsWith('=') && !v.startsWith('SUM')) headerRow.push({ col: c, name: v });
  }
  const teams = headerRow.map(h => h.name); // ← Excel column order preserved
  const pairs = [];
  teams.forEach((team, i) => {
    const dataRow = titleRow + 1 + i;
    teams.forEach((opp, j) => {
      if (i === j) return;
      const cell = ws[window.XLSX.utils.encode_cell({ r: dataRow, c: headerRow[j].col })];
      if (cell && (cell.v === 1 || cell.v === '1')) pairs.push({ home: team, away: opp });
    });
  });
  return { teams, pairs };
}

function parseSheet(ws, sheetName) {
  const ref = ws['!ref'];
  if (!ref) return [];
  const range = window.XLSX.utils.decode_range(ref);
  const titleRows = [];
  for (let r = 0; r <= range.e.r; r++) {
    const cell = ws[window.XLSX.utils.encode_cell({ r, c: 0 })];
    if (!cell) continue;
    const v = String(cell.v || '').toUpperCase();
    if (v.includes('T20') || v.includes('DIVISION') || v.includes('PREMIER')) titleRows.push({ r, v });
  }
  // Flat single-pool format (T30/F40): no division/pool titles in column A.
  // Detect a header row where column B contains "SHORT NAME" and team names start at column C.
  if (titleRows.length === 0) {
    for (let r = 0; r <= Math.min(5, range.e.r); r++) {
      for (const c of [0, 1]) {
        const cell = ws[window.XLSX.utils.encode_cell({ r, c })];
        const v = cell ? String(cell.v || '').toUpperCase().replace(/\s+/g, ' ').trim() : '';
        if (v.includes('SHORT') && v.includes('NAME')) {
          const { teams, pairs } = parsePool(ws, r);
          return teams.length > 0 ? [{ pool: null, teams, pairs }] : [];
        }
      }
    }
    return [];
  }
  if (sheetName.toLowerCase().includes('premier')) {
    const { teams, pairs } = parsePool(ws, titleRows[0].r + 1);
    return teams.length > 0 ? [{ pool: null, teams, pairs }] : [];
  }
  const results = [];
  titleRows.forEach(({ r, v }) => {
    const poolMatch = v.match(/POOL\s+([A-Z])/);
    const pool = poolMatch ? poolMatch[1] : (results.length === 0 ? 'A' : 'B');
    const { teams, pairs } = parsePool(ws, r + 1);
    if (teams.length > 0) results.push({ pool, teams, pairs });
  });
  return results;
}

// ─── Restriction Edit Modal ───────────────────────────────────────────────────
function RestrictionModal({ homeTeam, awayTeam, current, onSave, onClose }) {
  const [sel, setSel] = useState(current?.restriction || '');
  const [note, setNote] = useState(current?.note || '');
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card)', borderRadius: 12, width: 420,
          border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,.5)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>
              <span style={{ fontFamily: "'Roboto Mono',monospace", color: 'var(--accent)' }}>{homeTeam}</span>
              <span style={{ color: 'var(--dim)', margin: '0 8px' }}>vs</span>
              <span style={{ fontFamily: "'Roboto Mono',monospace", color: 'var(--accent)' }}>{awayTeam}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 3 }}>Add a scheduling restriction</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--mid)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            <div onClick={() => setSel('')}
              style={{ padding: '9px 14px', borderRadius: 7, cursor: 'pointer',
                border: `1.5px solid ${sel === '' ? 'var(--accent)' : 'var(--border)'}`,
                background: sel === '' ? 'rgba(0,200,150,.08)' : 'var(--surface)',
                display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 14, height: 14, borderRadius: 3,
                background: sel === '' ? 'var(--accent)' : 'var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'white' }}>
                {sel === '' ? '✓' : ''}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: sel === '' ? 'var(--accent)' : 'var(--mid)' }}>No restriction</span>
            </div>
            {RESTRICTION_OPTS.map(opt => {
              const col = RESTRICTION_COLORS[opt];
              return (
                <div key={opt} onClick={() => setSel(opt)}
                  style={{ padding: '9px 14px', borderRadius: 7, cursor: 'pointer',
                    border: `1.5px solid ${sel === opt ? col.border : 'var(--border)'}`,
                    background: sel === opt ? col.bg : 'var(--surface)',
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3,
                    background: sel === opt ? col.border : 'var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'white' }}>
                    {sel === opt ? '✓' : ''}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: sel === opt ? col.text : 'var(--mid)' }}>{opt}</span>
                </div>
              );
            })}
          </div>
          {(sel === 'Custom' || note) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', marginBottom: 6 }}>Note</div>
              <input className="input" placeholder="e.g. August only at Lasky"
                value={note} onChange={e => setNote(e.target.value)} style={{ width: '100%' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => onSave(sel || null, note)}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Import Modal ─────────────────────────────────────────────────────────────
function ImportModal({ onClose, divisions, allTeams, onImportDone }) {
  const [step, setStep]           = useState('upload');
  const [parsed, setParsed]       = useState([]);
  const [importLog, setImportLog] = useState([]);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const XLSX = await loadSheetJS();
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: 'array' });
      const results = [];
      for (const sheetName of wb.SheetNames) {
        const ws    = wb.Sheets[sheetName];
        const pools = parseSheet(ws, sheetName);
        if (pools.length === 0) continue;
        let matchedDiv = divisions.find(d => {
          const dn = d.name.toLowerCase().replace(/\s+/g, '');
          const sn = sheetName.trim().toLowerCase().replace(/\s+/g, '');
          return dn === sn || d.name.toLowerCase().includes(sheetName.trim().toLowerCase()) ||
            sheetName.trim().toLowerCase().includes(d.name.toLowerCase().replace('division', 'div'));
        });
        // Fallback for single-division series (e.g. auto-created "League" for T30/F40):
        // if no fuzzy match, use the only available division.
        if (!matchedDiv && divisions.length === 1) matchedDiv = divisions[0];
        pools.forEach(({ pool, teams, pairs }) => {
          const divTeams = matchedDiv ? allTeams.filter(t => t.division_id === matchedDiv.id) : allTeams;
          const matched  = teams.map(sn => ({
            shortName: sn,
            team: divTeams.find(t => t.short_name?.toLowerCase() === sn.toLowerCase()) || null,
          }));
          results.push({ sheetName, pool, teams, pairs, matchedDiv: matchedDiv || null,
            matched, unmatched: matched.filter(m => !m.team).map(m => m.shortName) });
          // Store Excel column order (short names) in localStorage
          if (matchedDiv) {
            lsSet(`matrixOrder_${matchedDiv.id}_${pool || 'none'}`, teams);
          }
        });
      }
      setParsed(results);
      setStep('preview');
    } catch (err) { toast.error('Failed to parse Excel: ' + err.message); }
  };

  const runImport = async () => {
    setStep('importing');
    const log = [];
    for (const block of parsed) {
      if (!block.matchedDiv) { log.push({ type: 'warn', msg: `⚠ Skipped "${block.sheetName}" — no matching division` }); continue; }
      const teamMap = {};
      block.matched.filter(m => m.team).forEach(({ shortName, team }) => { teamMap[shortName.toLowerCase()] = team.id; });
      const cells = block.pairs
        .map(({ home, away }) => ({ team1_id: teamMap[home.toLowerCase()], team2_id: teamMap[away.toLowerCase()], restriction: null, note: null }))
        .filter(c => c.team1_id && c.team2_id);
      if (cells.length > 0) {
        try {
          // For pool-specific imports: keep existing entries from OTHER pools,
          // replace entries only within this pool's teams.
          // For non-pool imports: replace all entries for the division.
          const existing = await matrixAPI.get(block.matchedDiv.id);
          const existingCells = existing.data.map(r => ({
            team1_id: r.team1_id, team2_id: r.team2_id,
            restriction: r.restriction, note: r.notes
          }));

          let finalCells;
          if (block.pool) {
            // Pool import: remove all existing entries where EITHER team is in this pool,
            // then add the new pool-only entries. This clears stale cross-pool pairs.
            const thisPoolTeamIds = new Set(
              block.matched.filter(m => m.team).map(m => m.team.id)
            );
            const otherPoolCells = existingCells.filter(r =>
              !thisPoolTeamIds.has(r.team1_id) && !thisPoolTeamIds.has(r.team2_id)
            );
            finalCells = [...otherPoolCells, ...cells];
            const removed = existingCells.length - otherPoolCells.length;
            if (removed > 0) log.push({ type: 'info', msg: `↺ Replaced ${removed} existing entries for ${block.matchedDiv.name} Pool ${block.pool}` });
          } else {
            // Non-pool import: replace all entries for this division
            finalCells = cells;
          }

          await matrixAPI.save(block.matchedDiv.id, finalCells);
          log.push({ type: 'ok', msg: `✓ ${Math.round(cells.length / 2)} games imported for ${block.matchedDiv.name}${block.pool ? ` Pool ${block.pool}` : ''}` });
        } catch (err) { log.push({ type: 'error', msg: `✕ Failed: ${err.message}` }); }
      }
      if (block.unmatched.length > 0) log.push({ type: 'warn', msg: `⚠ Unmatched teams: ${block.unmatched.join(', ')}` });
    }
    setImportLog(log);
    setStep('done');
    onImportDone();
  };

  const totalPairs = parsed.reduce((s, b) => s + b.pairs.length, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.7)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card)', borderRadius: 12, width: '100%', maxWidth: 780,
          display: 'flex', flexDirection: 'column', border: '1px solid var(--border)',
          boxShadow: '0 24px 64px rgba(0,0,0,.5)', marginBottom: 40 }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1, borderRadius: '12px 12px 0 0' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>📥 Import Matchup Matrix from Excel</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--mid)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          {step === 'upload' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📊</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Select your Excel file</div>
              <div style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.7, maxWidth: 440, margin: '0 auto 24px' }}>
                Sheets: Premier, Div 1, Div 2, Div 3. Each sheet has Pool A and Pool B grids. A cell value of <strong>1</strong> means those teams play.
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFile} />
              <button className="btn btn-primary" onClick={() => fileRef.current.click()} style={{ padding: '10px 28px' }}>📂 Choose Excel File</button>
            </div>
          )}
          {step === 'preview' && (
            <div>
              <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(0,200,150,.1)', border: '1px solid rgba(0,200,150,.3)', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>{parsed.length} pools found</span>
                <span style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(100,149,237,.1)', border: '1px solid rgba(100,149,237,.3)', fontSize: 12, fontWeight: 700, color: 'var(--blue)' }}>{Math.round(totalPairs / 2)} games</span>
              </div>
              {parsed.map((b, i) => (
                <div key={i} style={{ marginBottom: 10, padding: '12px 14px', borderRadius: 8, background: 'var(--surface)',
                    border: `1px solid ${b.unmatched.length > 0 ? 'rgba(239,68,68,.3)' : b.matchedDiv ? 'rgba(0,200,150,.25)' : 'rgba(245,166,35,.3)'}` }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text)', minWidth: 120 }}>{b.sheetName}{b.pool ? ` · Pool ${b.pool}` : ''}</span>
                    {b.matchedDiv
                      ? <span style={{ fontSize: 10, background: 'rgba(0,200,150,.15)', color: 'var(--accent)', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>✓ {b.matchedDiv.name}</span>
                      : <span style={{ fontSize: 10, background: 'rgba(245,166,35,.15)', color: 'var(--gold)', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>⚠ No match</span>}
                    <span style={{ fontSize: 11, color: 'var(--mid)' }}>{b.teams.length} teams · {Math.round(b.pairs.length / 2)} games</span>
                    {b.unmatched.length > 0 && <span style={{ fontSize: 10, color: 'var(--red)', fontStyle: 'italic' }}>⚠ Unmatched: {b.unmatched.join(', ')}</span>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 4 }}>
                    {b.matched.map(({ shortName, team }) => (
                      <span key={shortName} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                          fontFamily: "'Roboto Mono',monospace", textAlign: 'center', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          background: team ? 'rgba(0,200,150,.1)' : 'rgba(239,68,68,.1)',
                          color: team ? 'var(--accent)' : 'var(--red)',
                          border: `1px solid ${team ? 'rgba(0,200,150,.3)' : 'rgba(239,68,68,.3)'}` }}>
                        {team ? '✓ ' : '✕ '}{shortName}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner size={36} />
              <div style={{ marginTop: 16, fontSize: 14, color: 'var(--mid)' }}>Importing…</div>
            </div>
          )}
          {step === 'done' && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', marginBottom: 14 }}>✅ Import complete</div>
              {importLog.map((e, i) => (
                <div key={i} style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 6,
                    background: e.type === 'ok' ? 'rgba(0,200,150,.08)' : e.type === 'error' ? 'rgba(239,68,68,.08)' : 'rgba(245,166,35,.08)',
                    color: e.type === 'ok' ? 'var(--accent)' : e.type === 'error' ? 'var(--red)' : 'var(--gold)',
                    border: `1px solid ${e.type === 'ok' ? 'rgba(0,200,150,.2)' : e.type === 'error' ? 'rgba(239,68,68,.2)' : 'rgba(245,166,35,.2)'}` }}>
                  {e.msg}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)',
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            position: 'sticky', bottom: 0, background: 'var(--card)', borderRadius: '0 0 12px 12px' }}>
          {step === 'done'    && <button className="btn btn-primary" onClick={onClose}>✓ Close</button>}
          {step === 'preview' && <><button className="btn btn-ghost" onClick={() => setStep('upload')}>← Back</button><button className="btn btn-primary" onClick={runImport}>Import ({Math.round(totalPairs / 2)} games)</button></>}
          {step === 'upload'  && <button className="btn btn-ghost" onClick={onClose}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Matrix Grid ─────────────────────────────────────────────────────────────
function MatrixGrid({ gridTeams, matrix, canEdit, onCellClick }) {
  if (gridTeams.length < 2) return null;

  // Build a set of team IDs that are in this grid
  // Used to filter out cross-pool pairs that should not appear in this pool's grid
  const gridTeamIds = new Set(gridTeams.map(t => t.id));

  const getState = (rowTeam, colTeam) => {
    if (rowTeam.id === colTeam.id) return { type: 'SELF' };
    const entry = matrix[`${rowTeam.id}:${colTeam.id}`] || matrix[`${colTeam.id}:${rowTeam.id}`];
    if (!entry) return { type: 'NO_PLAY' };
    // Cross-pool guard: if both teams must be in this grid's pool, but either ID
    // is not in gridTeamIds, this is a stale cross-pool pair — treat as NO_PLAY
    // (This happens when team pool assignments changed after matrix was imported)
    if (!gridTeamIds.has(rowTeam.id) || !gridTeamIds.has(colTeam.id)) return { type: 'NO_PLAY' };
    const r = entry.restriction;
    if (r && r !== 'None') return { type: 'RESTRICTED', restriction: r, note: entry.note };
    return { type: 'PLAYS' };
  };

  // Per-row stats: games count + doesn't-play list
  const rowStats = {};
  gridTeams.forEach(rowTeam => {
    let games = 0;
    const noPlay = [];
    gridTeams.forEach(colTeam => {
      if (rowTeam.id === colTeam.id) return;
      const st = getState(rowTeam, colTeam);
      if (st.type === 'PLAYS' || st.type === 'RESTRICTED') games++;
      else if (st.type === 'NO_PLAY') noPlay.push(colTeam.short_name);
    });
    rowStats[rowTeam.id] = { games, noPlay };
  });

  const n = gridTeams.length;
  const cellW    = n > 14 ? 28 : n > 10 ? 34 : 42;
  const cellH    = n > 14 ? 24 : n > 10 ? 28 : 32;
  const fs       = n > 14 ? 8  : n > 10 ? 9  : 10;
  const vertical = n > 12;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: fs }}>
        <thead>
          <tr>
            <th style={{ width: 70, minWidth: 70, padding: '4px 8px', textAlign: 'left',
                color: 'var(--dim)', fontWeight: 600, borderBottom: '2px solid var(--border)',
                borderRight: '2px solid var(--border)', fontSize: 9, whiteSpace: 'nowrap' }}>
              ↓ Home · Away →
            </th>
            {gridTeams.map(t => (
              <th key={t.id} style={{ width: cellW, minWidth: cellW, padding: '4px 2px', textAlign: 'center',
                  color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace", fontWeight: 700,
                  borderBottom: '2px solid var(--border)', fontSize: fs, whiteSpace: 'nowrap',
                  writingMode: vertical ? 'vertical-lr' : 'horizontal-tb',
                  height: vertical ? 60 : 'auto' }}>
                {t.short_name}
              </th>
            ))}
            {/* Games — always horizontal */}
            <th style={{ width: 38, minWidth: 38, padding: '4px 4px', textAlign: 'center',
                color: 'var(--blue)', fontWeight: 800, borderBottom: '2px solid var(--border)',
                borderLeft: '2px solid var(--border)', fontSize: fs, whiteSpace: 'nowrap',
                writingMode: 'horizontal-tb', height: vertical ? 60 : 'auto', verticalAlign: 'bottom' }}>
              Games
            </th>
            {/* Doesn't play — always horizontal */}
            <th style={{ padding: '4px 8px', textAlign: 'left',
                color: 'var(--gold)', fontWeight: 800, borderBottom: '2px solid var(--border)',
                borderLeft: '1px solid var(--border)', fontSize: fs, whiteSpace: 'nowrap',
                writingMode: 'horizontal-tb', height: vertical ? 60 : 'auto', verticalAlign: 'bottom' }}>
              Doesn't play
            </th>
          </tr>
        </thead>
        <tbody>
          {gridTeams.map(rowTeam => (
            <tr key={rowTeam.id}>
              <td style={{ padding: '2px 8px', fontFamily: "'Roboto Mono',monospace", fontWeight: 700,
                  color: 'var(--accent)', borderRight: '2px solid var(--border)', whiteSpace: 'nowrap',
                  fontSize: fs, borderBottom: '1px solid var(--border)' }}>
                {rowTeam.short_name}
              </td>
              {gridTeams.map(colTeam => {
                const state = getState(rowTeam, colTeam);
                let bg, border, content, cursor, title;
                if (state.type === 'SELF') {
                  bg     = 'repeating-linear-gradient(45deg,rgba(245,166,35,.25) 0,rgba(245,166,35,.25) 2px,var(--surface) 2px,var(--surface) 8px)';
                  border = '1px solid rgba(245,166,35,.3)';
                  content = null; cursor = 'default'; title = '';
                } else if (state.type === 'PLAYS') {
                  bg     = 'rgba(0,200,150,.12)';
                  border = '1px solid rgba(0,200,150,.35)';
                  content = <span style={{ fontSize: fs + 1, fontWeight: 800, color: 'var(--accent)' }}>1</span>;
                  cursor = canEdit ? 'pointer' : 'default';
                  title  = `${rowTeam.short_name} vs ${colTeam.short_name} — click to restrict`;
                } else if (state.type === 'RESTRICTED') {
                  const col = RESTRICTION_COLORS[state.restriction] || RESTRICTION_COLORS['Custom'];
                  bg     = col.bg; border = `1px solid ${col.border}`;
                  content = <span style={{ fontSize: fs - 1, fontWeight: 700, color: col.text, textAlign: 'center', lineHeight: 1.1 }}>
                    {state.restriction === 'Must not play' ? '✕' : state.restriction.replace(' only', '')}
                  </span>;
                  cursor = canEdit ? 'pointer' : 'default';
                  title  = `${rowTeam.short_name} vs ${colTeam.short_name}: ${state.restriction}${state.note ? ' — ' + state.note : ''}`;
                } else {
                  // NO_PLAY — orange
                  bg     = 'rgba(245,166,35,.2)';
                  border = '1px solid rgba(245,166,35,.35)';
                  content = null; cursor = 'default';
                  title  = `${rowTeam.short_name} vs ${colTeam.short_name} — not scheduled`;
                }
                return (
                  <td key={colTeam.id} style={{ padding: '2px', borderBottom: '1px solid var(--border)' }}>
                    <div onClick={() => (state.type === 'PLAYS' || state.type === 'RESTRICTED') && canEdit ? onCellClick(rowTeam, colTeam, state) : null}
                      title={title}
                      style={{ width: cellW, height: cellH, borderRadius: 2, background: bg, border,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor }}>
                      {content}
                    </div>
                  </td>
                );
              })}
              {/* Games count */}
              <td style={{ padding: '2px 4px', borderBottom: '1px solid var(--border)',
                  borderLeft: '2px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>
                <span style={{ fontSize: fs + 1, fontWeight: 800, color: 'var(--blue)', fontFamily: "'Roboto Mono',monospace" }}>
                  {rowStats[rowTeam.id]?.games ?? 0}
                </span>
              </td>
              {/* Doesn't play */}
              <td style={{ padding: '2px 8px', borderBottom: '1px solid var(--border)',
                  borderLeft: '1px solid var(--border)', verticalAlign: 'middle' }}>
                {rowStats[rowTeam.id]?.noPlay.length > 0
                  ? <span style={{ fontSize: fs - 1, color: 'var(--gold)', fontFamily: "'Roboto Mono',monospace", whiteSpace: 'nowrap' }}>
                      {rowStats[rowTeam.id].noPlay.join(', ')}
                    </span>
                  : <span style={{ fontSize: fs - 1, color: 'var(--dim)', fontStyle: 'italic' }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MatrixPage() {
  const { canEdit } = useAuthStore();
  const [seriesList,          setSeriesList]          = useState([]);
  const [divisions,           setDivisions]           = useState([]);
  const [allTeams,            setAllTeams]            = useState([]);
  const [teams,               setTeams]               = useState([]);
  const [selSeries,           setSelSeries]           = useState('');
  const [selDivision,         setSelDivision]         = useState('');
  const [selPool,             setSelPool]             = useState('');
  const [matrix,              setMatrix]              = useState({});
  const [loading,             setLoading]             = useState(false);
  const [saving,              setSaving]              = useState(false);
  const [showImport,          setShowImport]          = useState(false);
  const [editModal,           setEditModal]           = useState(null);
  const [showClearConfirm,    setShowClearConfirm]    = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  // teamOrder: { '<divId>_<pool|none>': [teamId, ...] } — Excel column order
  const [teamOrder, setTeamOrder] = useState({});

  useEffect(() => { loadSeries(); }, []);
  useEffect(() => { if (selSeries) loadDivisions(selSeries); }, [selSeries]);
  useEffect(() => { if (selDivision) loadTeamsAndMatrix(); }, [selDivision]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setSelSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (sid) => {
    try {
      const { data } = await divisionsAPI.list(sid);
      setDivisions(data);
      const { data: td } = await teamsAPI.list({ series_id: sid });
      setAllTeams(td);
      if (data.length > 0) setSelDivision(data[0].id);
    } catch {}
  };

  const loadTeamsAndMatrix = async () => {
    setLoading(true);
    setSelPool('');
    try {
      const { data: teamData }   = await teamsAPI.list({ series_id: selSeries, division_id: selDivision });
      const { data: matrixData } = await matrixAPI.get(selDivision);

      // Build matrix map
      const map = {};
      matrixData.forEach(r => {
        map[`${r.team1_id}:${r.team2_id}`] = { restriction: r.restriction || null, note: r.notes || r.note || '' };
      });
      setTeams(teamData);
      setMatrix(map);

      // Build ordered team lists per pool from localStorage (short names → IDs)
      // localStorage stores short name arrays in Excel column order
      const shortToId = {};
      teamData.forEach(t => { shortToId[(t.short_name || '').toUpperCase()] = t.id; });

      const orderForPool = (pool) => {
        // 1. Try localStorage (set during Excel import — most accurate)
        const stored = lsGet(`matrixOrder_${selDivision}_${pool || 'none'}`);
        if (stored && Array.isArray(stored)) {
          const ids = stored.map(sn => shortToId[(sn || '').toUpperCase()]).filter(Boolean);
          // Append any pool members missing from stored order
          const poolMembers = teamData.filter(t => pool ? t.pool === pool : !t.pool);
          poolMembers.forEach(t => { if (!ids.includes(t.id)) ids.push(t.id); });
          return ids;
        }
        // 2. Fallback: derive from matrix — team1_id first-appearances, filtered to this pool only
        const poolIds = new Set(teamData.filter(t => pool ? t.pool === pool : !t.pool).map(t => t.id));
        const seen = [];
        matrixData.forEach(r => {
          if (poolIds.has(r.team1_id) && !seen.includes(r.team1_id)) seen.push(r.team1_id);
        });
        [...poolIds].forEach(id => { if (!seen.includes(id)) seen.push(id); });
        return seen;
      };

      const pools = [...new Set(teamData.map(t => t.pool).filter(Boolean))];
      const newOrder = {};
      if (pools.length > 0) {
        pools.forEach(p => { newOrder[`${selDivision}_${p}`] = orderForPool(p); });
      } else {
        newOrder[`${selDivision}_none`] = orderForPool(null);
      }
      setTeamOrder(prev => ({ ...prev, ...newOrder }));
    } catch { toast.error('Failed to load matrix'); }
    finally { setLoading(false); }
  };

  // Return teams in Excel column order for a given pool
  const orderedPoolTeams = (pool) => {
    const key = `${selDivision}_${pool || 'none'}`;
    const ids = teamOrder[key];
    if (!ids || ids.length === 0) {
      // No order yet — return pool members alphabetically as fallback
      return teams.filter(t => pool ? t.pool === pool : !t.pool);
    }
    const byId = {};
    teams.forEach(t => { byId[t.id] = t; });
    const result = ids.map(id => byId[id]).filter(Boolean);
    // Append stragglers
    teams.filter(t => pool ? t.pool === pool : !t.pool).forEach(t => {
      if (!ids.includes(t.id)) result.push(t);
    });
    return result;
  };

  const handleSaveRestriction = async (restriction, note) => {
    if (!editModal) return;
    const { homeTeam, awayTeam } = editModal;
    const key    = `${homeTeam.id}:${awayTeam.id}`;
    const revKey = `${awayTeam.id}:${homeTeam.id}`;
    const newMatrix = { ...matrix };
    if (!restriction) {
      if (newMatrix[key])    newMatrix[key]    = { restriction: null, note: '' };
      if (newMatrix[revKey]) newMatrix[revKey] = { restriction: null, note: '' };
    } else {
      if      (newMatrix[key])    newMatrix[key]    = { restriction, note };
      else if (newMatrix[revKey]) newMatrix[revKey] = { restriction, note };
      else                        newMatrix[key]    = { restriction, note };
    }
    setMatrix(newMatrix);
    setEditModal(null);
    setSaving(true);
    try {
      const cells = Object.entries(newMatrix).map(([k, v]) => {
        const [team1_id, team2_id] = k.split(':');
        return { team1_id, team2_id, restriction: v.restriction || null, note: v.note || null };
      });
      await matrixAPI.save(selDivision, cells);
      toast.success('Saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const clearDivision = async () => {
    setShowClearConfirm(false);
    setSaving(true);
    try {
      await matrixAPI.save(selDivision, []);
      setMatrix({});
      toast.success('Division matrix cleared');
    } catch { toast.error('Failed to clear'); }
    finally { setSaving(false); }
  };

  const clearAllDivisions = async () => {
    setShowClearAllConfirm(false);
    setSaving(true);
    try {
      await Promise.all(divisions.map(d => matrixAPI.save(d.id, [])));
      setMatrix({});
      setTeamOrder({});
      toast.success('All divisions cleared');
    } catch { toast.error('Failed to clear'); }
    finally { setSaving(false); }
  };

  // Derived
  const selDivObj  = divisions.find(d => d.id === selDivision);
  const availPools = selDivObj?.pools?.length > 0 ? selDivObj.pools : [];
  const hasPools   = availPools.length > 0;
  const fixturePairs = Object.keys(matrix).length;
  const fixtureGames = Math.round(fixturePairs / 2);

  const restrictions = Object.entries(matrix)
    .filter(([, v]) => v.restriction && v.restriction !== 'None')
    .map(([key, val]) => {
      const [t1id, t2id] = key.split(':');
      return { t1: teams.find(t => t.id === t1id), t2: teams.find(t => t.id === t2id), ...val };
    }).filter(r => r.t1 && r.t2);

  const visibleRestrictions = selPool
    ? restrictions.filter(r => r.t1.pool === selPool || r.t2.pool === selPool)
    : restrictions;

  const pairsInPool = (pool) => {
    const pt = new Set(orderedPoolTeams(pool).map(t => t.id));
    return Object.entries(matrix).filter(([k]) => {
      const [a, b] = k.split(':');
      return pt.has(a) && pt.has(b);
    }).length;
  };

  return (
    <div>
      <PageHeader icon="⊞" title="Matchup Matrix"
        subtitle="Excel-style fixture matrix. Click a green cell to add a restriction."
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {saving && <Spinner size={14} />}
            {canEdit() && selDivision && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 12 }}
                onClick={() => setShowClearConfirm(true)}>☐ Clear Division</button>
            )}
            {canEdit() && divisions.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 12 }}
                onClick={() => setShowClearAllConfirm(true)}>☐ Clear All Divisions</button>
            )}
            {canEdit() && (
              <button className="btn btn-ghost" onClick={() => setShowImport(true)} style={{ fontSize: 13 }}>
                📥 Import Excel
              </button>
            )}
          </div>
        }
      />

      {/* Selector bar */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input select" style={{ width: 180 }} value={selSeries}
          onChange={e => { setSelSeries(e.target.value); setSelDivision(''); setTeams([]); setMatrix({}); }}>
          {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
        </select>
        <select className="input select" style={{ width: 180 }} value={selDivision}
          onChange={e => setSelDivision(e.target.value)}>
          <option value="">Select division...</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {hasPools && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn btn-sm ${selPool === '' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSelPool('')}>All Pools</button>
            {availPools.map(p => (
              <button key={p} className={`btn btn-sm ${selPool === p ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSelPool(p)}>
                Pool {p} <span style={{ opacity: .6, marginLeft: 3 }}>({orderedPoolTeams(p).length})</span>
              </button>
            ))}
          </div>
        )}
        {teams.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--mid)' }}>
            {teams.length} teams · {fixtureGames} games · {restrictions.length} restriction{restrictions.length !== 1 ? 's' : ''}
          </span>
        )}
        {/* Cross-pool alert — shown when matrix has stale cross-pool entries */}
        {hasPools && (() => {
          const allPoolIds = new Set(teams.map(t => t.id));
          const crossPoolCount = Object.keys(matrix).filter(k => {
            const [a, b] = k.split(':');
            if (!allPoolIds.has(a) || !allPoolIds.has(b)) return false;
            const ta = teams.find(t => t.id === a);
            const tb = teams.find(t => t.id === b);
            return ta && tb && ta.pool !== tb.pool;
          }).length;
          return crossPoolCount > 0 ? (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 10px',
                background:'rgba(245,158,11,.1)', border:'1px solid rgba(245,158,11,.3)',
                borderRadius:6, fontSize:11, color:'#B45309' }}>
              ⚠ {crossPoolCount} cross-pool pair{crossPoolCount!==1?'s':''} in matrix
              <button className="btn btn-sm" style={{ fontSize:10, padding:'2px 8px',
                  background:'rgba(245,158,11,.2)', border:'1px solid rgba(245,158,11,.4)', color:'#92400E' }}
                onClick={() => setShowImport(true)}>Re-import to fix →</button>
            </div>
          ) : null;
        })()}
        {/* Legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { color: 'rgba(0,200,150,.15)',  border: 'rgba(0,200,150,.4)',   label: '1 = plays' },
            { color: 'rgba(245,166,35,.2)',  border: 'rgba(245,166,35,.35)', label: 'orange = not scheduled' },
            { color: 'rgba(239,68,68,.2)',   border: 'rgba(239,68,68,.4)',   label: 'red = must not play' },
            { color: 'rgba(59,130,246,.15)', border: 'rgba(59,130,246,.4)',  label: 'restricted' },
          ].map(l => (
            <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: l.color, border: `1px solid ${l.border}`, flexShrink: 0 }} />
              <span style={{ color: 'var(--mid)' }}>{l.label}</span>
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner size={28} /></div>
      ) : !selDivision ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--mid)', fontSize: 13 }}>Select a series and division to view the matrix.</div>
      ) : teams.length < 2 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--mid)', fontSize: 13 }}>Need at least 2 teams.</div>
      ) : fixturePairs === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 500, margin: '0 auto' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⊞</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{selDivObj?.name} — No fixture matrix yet</div>
          <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
            {hasPools ? `This division has ${availPools.length} pools. ` : ''}Import the Excel fixture grid to populate the matchup matrix.
          </div>
          <button className="btn btn-primary" onClick={() => setShowImport(true)}>📥 Import Excel</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 16, alignItems: 'start' }}>
          {/* Grid area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {!hasPools && (
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  {selDivObj?.name} · {teams.length} teams · {fixtureGames} games
                  {canEdit() && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--dim)', fontWeight: 400, textTransform: 'none' }}>Click green cell to add restriction</span>}
                </div>
                <MatrixGrid gridTeams={orderedPoolTeams(null)} matrix={matrix} canEdit={canEdit()}
                  onCellClick={(ht, at, st) => setEditModal({ homeTeam: ht, awayTeam: at, state: st })} />
              </div>
            )}
            {hasPools && (selPool !== ''
              ? (() => {
                  const pt = orderedPoolTeams(selPool);
                  const pip = pairsInPool(selPool);
                  return (
                    <div className="card" style={{ padding: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        {selDivObj?.name} · Pool {selPool} · {pt.length} teams · {Math.round(pip / 2)} games
                        {canEdit() && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--dim)', fontWeight: 400, textTransform: 'none' }}>Click green cell to add restriction</span>}
                      </div>
                      {(() => {
                        const ptIds = new Set(pt.map(t => t.id));
                        const crossPool = Object.keys(matrix).filter(k => {
                          const [a, b] = k.split(':');
                          return (ptIds.has(a) && !ptIds.has(b)) || (!ptIds.has(a) && ptIds.has(b));
                        });
                        return crossPool.length > 0 ? (
                          <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6,
                              background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.3)',
                              fontSize: 11, color: '#B45309', lineHeight: 1.5 }}>
                            ⚠ <strong>{crossPool.length} cross-pool pair{crossPool.length !== 1 ? 's' : ''} detected</strong> — the matrix has entries between teams from different pools.
                            This usually means pool assignments changed after import. Cross-pool matchups are hidden below.
                            Re-import the updated Excel file to fix the matrix.
                          </div>
                        ) : null;
                      })()}
                      {pt.length < 2
                        ? <div style={{ fontSize: 12, color: 'var(--dim)', padding: '20px 0' }}>No teams in Pool {selPool}</div>
                        : <MatrixGrid gridTeams={pt} matrix={matrix} canEdit={canEdit()}
                            onCellClick={(ht, at, st) => setEditModal({ homeTeam: ht, awayTeam: at, state: st })} />}
                    </div>
                  );
                })()
              : availPools.map(p => {
                  const pt = orderedPoolTeams(p);
                  const pip = pairsInPool(p);
                  return (
                    <div key={p} className="card" style={{ padding: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{selDivObj?.name} · Pool {p} · {pt.length} teams · {Math.round(pip / 2)} games</span>
                        {pip === 0 && <span style={{ fontSize: 10, background: 'rgba(245,166,35,.12)', color: 'var(--gold)', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>⚠ Not imported yet</span>}
                      </div>
                      {(() => {
                        const ptIds = new Set(pt.map(t => t.id));
                        const crossPool = Object.keys(matrix).filter(k => {
                          const [a, b] = k.split(':');
                          return (ptIds.has(a) && !ptIds.has(b)) || (!ptIds.has(a) && ptIds.has(b));
                        });
                        return crossPool.length > 0 ? (
                          <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6,
                              background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.3)',
                              fontSize: 11, color: '#B45309', lineHeight: 1.5 }}>
                            ⚠ <strong>{crossPool.length} cross-pool pair{crossPool.length !== 1 ? 's' : ''} detected</strong> — pool assignments changed since import. Cross-pool matchups are hidden. Re-import Excel to fix.
                          </div>
                        ) : null;
                      })()}
                      {pt.length < 2
                        ? <div style={{ fontSize: 12, color: 'var(--dim)', padding: '10px 0' }}>No teams in Pool {p}</div>
                        : <MatrixGrid gridTeams={pt} matrix={matrix} canEdit={canEdit()}
                            onCellClick={(ht, at, st) => setEditModal({ homeTeam: ht, awayTeam: at, state: st })} />}
                    </div>
                  );
                })
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card" style={{ padding: 14 }}>
              <SectionTitle color="var(--purple)">Restrictions</SectionTitle>
              {visibleRestrictions.length === 0
                ? <div style={{ fontSize: 11, color: 'var(--dim)', padding: '6px 0' }}>None set.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {visibleRestrictions.map((r, i) => {
                      const col = RESTRICTION_COLORS[r.restriction] || RESTRICTION_COLORS['Custom'];
                      return (
                        <div key={i} style={{ padding: '7px 9px', background: col.bg, border: `1px solid ${col.border}`, borderRadius: 6 }}>
                          <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace" }}>{r.t1.short_name}</span>
                            <span style={{ fontSize: 9, color: 'var(--dim)' }}>vs</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace" }}>{r.t2.short_name}</span>
                          </div>
                          <div style={{ fontSize: 10, color: col.text, fontWeight: 700 }}>{r.restriction}</div>
                          {r.note && <div style={{ fontSize: 9, color: 'var(--mid)', marginTop: 1 }}>{r.note}</div>}
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
            <div className="card" style={{ padding: 14 }}>
              <SectionTitle color="var(--accent)">Summary</SectionTitle>
              <div style={{ fontSize: 11, color: 'var(--mid)', lineHeight: 1.9 }}>
                <div>Total games: <strong style={{ color: 'var(--text)' }}>{fixtureGames}</strong></div>
                <div>Restrictions: <strong style={{ color: 'var(--text)' }}>{restrictions.length}</strong></div>
                {hasPools && availPools.map(p => (
                  <div key={p}>Pool {p}: <strong style={{ color: 'var(--text)' }}>{orderedPoolTeams(p).length} teams</strong></div>
                ))}
              </div>
            </div>
            <div className="card" style={{ padding: 14 }}>
              <SectionTitle color="var(--mid)">How to use</SectionTitle>
              <div style={{ fontSize: 11, color: 'var(--mid)', lineHeight: 1.8 }}>
                <div>• <strong style={{ color: 'var(--accent)' }}>Green 1</strong> = game scheduled</div>
                <div>• <strong style={{ color: 'var(--gold)' }}>Orange</strong> = no match</div>
                <div>• Click green cell to restrict</div>
                <div>• Both triangles same colour</div>
                <div>• Import Excel to populate</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restriction edit modal */}
      {editModal && (
        <RestrictionModal homeTeam={editModal.homeTeam.short_name} awayTeam={editModal.awayTeam.short_name}
          current={editModal.state} onSave={handleSaveRestriction} onClose={() => setEditModal(null)} />
      )}

      {/* Clear division confirm */}
      {showClearConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowClearConfirm(false); }}>
          <div style={{ background: 'var(--card)', borderRadius: 12, width: 380, padding: 28,
              border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,.5)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginBottom: 10 }}>☐ Clear Division Matrix</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
              This will remove all <strong>{fixtureGames} games</strong> and restrictions for <strong>{selDivObj?.name}</strong>. Cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={clearDivision}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {/* Clear all divisions confirm */}
      {showClearAllConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowClearAllConfirm(false); }}>
          <div style={{ background: 'var(--card)', borderRadius: 12, width: 400, padding: 28,
              border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,.5)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginBottom: 10 }}>☐ Clear All Divisions</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
              This will remove all fixture pairs and restrictions for every division ({divisions.length} total). Cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setShowClearAllConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={clearAllDivisions}>Clear All</button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} divisions={divisions}
          allTeams={allTeams} onImportDone={loadTeamsAndMatrix} />
      )}
    </div>
  );
}
